/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
*
* NVIDIA CORPORATION and its licensors retain all intellectual property
* and proprietary rights in and to this software, related documentation
* and any modifications thereto.  Any use, reproduction, disclosure or
* distribution of this software and related documentation without an express
* license agreement from NVIDIA CORPORATION is strictly prohibited.
*/

/** This file was automatically generated. DO NOT MODIFY IT BY HAND. */

/* eslint-disable @typescript-eslint/naming-convention */

export module LocalgamesTypes {

/** This object was automatically generated by json-schema-to-typescript. DO NOT MODIFY IT BY HAND. */

export interface LocalGames {
    [k: string]: any;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "Application".
 */
export interface Application {
    /**
     * Machine-unique application installation ID
     */
    localId: number;
    /**
     * Application ID in Unified CMS: https://wiki.nvidia.com/engwiki/index.php/Unified_CMS
     */
    cmsId: number;
    /**
     * Application shortname, unique title identifier in OPS infrastructure.
     */
    shortName: string;
    /**
     * Non-localized original application name.
     */
    displayName: string;
    /**
     * Full path to the application installation directory.
     */
    installDirectory: string;
    /**
     * Defines if the application can have settings and is subject to optimization. Applications without this flag are not shown in GFE UI.
     */
    regularOpsSupported: boolean;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "VOPSSettingInfo".
 */
export interface VOPSSettingInfo {
    /**
     * Setting name
     */
    name: string;
    /**
     * URL to VOPS image with the setting. The URL could start with file:/// and point to local filesystem.
     */
    image: string;
    regionRect: regionRect;
    textRect: textRect;
    textInfo: textInfo;
}
/**
 * Defines rectangle that demonstrates the setting
 */
export interface regionRect {
    height: number;
    width: number;
    top: number;
    left: number;
}
/**
 * Defines rectangle that holds setting description
 */
export interface textRect {
    height: number;
    width: number;
}
/**
 * Holds text in various languages
 */
export interface textInfo {
    [k: string]: string;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "BatteryBoostState".
 */
export interface BatteryBoostState {
    /**
     * Defines if BatteryBoost is enabled
     */
    enabled: boolean;
    /**
     * BatteryBoost Framerate limit
     */
    currentLimit: number;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "WhisperModeState".
 */
export interface WhisperModeState {
    /**
     * Defines if WhisperMode is enabled for the system
     */
    enabled: boolean;
    /**
     * Defines if WhisperMode is supported for system
     */
    supported: boolean;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "FeatureSupportCriterion".
 */
export interface FeatureSupportCriterion {
    /**
     * Criterion name
     */
    name: string;
    /**
     * User-visible message for criterion
     */
    message: string;
    /**
     * Defines criterion state
     */
    state: boolean;
    /**
     * If true then this requirement is optional, otherwise it is mandatory
     */
    isRecommendation: boolean;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "FeatureSupportState".
 */
export interface FeatureSupportState {
    /**
     * Describes individual feature states
     */
    states: FeatureSupportCriterion[];
    /**
     * Defines if feature is supported
     */
    overallState: boolean;
    /**
     * UI tile name
     */
    featureTile: string;
    /**
     * Localized header name
     */
    header: string;
    /**
     * Localized feature tile message
     */
    message: string;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "SettingTranslation".
 */
export interface SettingTranslation {
    /**
     * Setting name
     */
    name: string;
    /**
     * Setting name translation
     */
    nameTranslation: string;
    /**
     * Setting values translation
     */
    values: {
        /**
         * Setting value name
         */
        valueName: string;
        /**
         * Setting value translation
         */
        valueTranslation: string;
    }[];
}
/**
 * Describes setting value, name and type.
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "SettingValue".
 */
export interface SettingValue {
    /**
     * Non-localized setting name
     */
    name: string;
    type: Type;
    /**
     * Non-localized setting value.
     */
    value: string;
}
/**
 * Defines the settings space tweak
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "SettingsTweak".
 */
export interface SettingsTweak {
    /**
     * Resolution value to use
     */
    resolutionValue: string;
    /**
     * Display mode value to use
     */
    displayModeValue: string;
}
/**
 * Defines the settings recommendation in settings space
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "SettingsRecommendation".
 */
export interface SettingsRecommendation {
    /**
     * Contains index of the recommended settings set in setting space
     */
    recommendedIndex: number;
    /**
     * Defines if it is possible to provide good playing experience with any settings set
     */
    belowMinspec: boolean;
}
/**
 * Defines possible application settings for the specified tweaks
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "SettingsSpace".
 */
export interface SettingsSpace {
    /**
     * Settings sets available for selected Resolution/DisplayMode combination
     */
    settingsSets: SettingValue[][];
    recommendationAC?: SettingsRecommendation;
    recommendationDC?: SettingsRecommendation;
}
/**
 * Describes position on the OPS Slider
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "SliderPosition".
 */
export interface SliderPosition {
    tweak: SettingsTweak;
    /**
     * Index of setting set in settings space
     */
    settingsIndex: number;
}
/**
 * Describes the OPS Slider resolution in resolution dropdown.
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "SliderResolution".
 */
export interface SliderResolution {
    /**
     * In-game setting value used for this resolution. Do not assume format of this value.
     */
    value: string;
    /**
     * Width of the image in pixels.
     */
    width: number;
    /**
     * Height of the image in pixels.
     */
    height: number;
    /**
     * Set to 'true' if resolution is hypersampling/DSR resolution.
     */
    isHypersampling: boolean;
}
/**
 * Describes OPS Slider dropdown settings
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "SliderSettings".
 */
export interface SliderSettings {
    /**
     * Name used for resolution setting.
     */
    resolutionName: string;
    /**
     * Resolutions in slider dropdown
     */
    resolutions: SliderResolution[];
    /**
     * Index of the recommended value in 'resolutions' array.
     */
    resolutionRecommendation: number;
    /**
     * Name used for display mode setting.
     */
    displayModeName: string;
    /**
     * Display mode values
     */
    displayModes: string[];
    /**
     * Index of the recommended value in 'displayModes' array.
     */
    displayModeRecommendation: number;
}
/**
 * Describes application profile (or virtual profile) state
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "SettingsProfileState".
 */
export interface SettingsProfileState {
    currentOptimizationState: OptimizationState;
    currentPosition?: SliderPosition;
    targetOptimizationState: OptimizationState;
    targetPosition?: SliderPosition;
    targetSettings?: SettingValue[];
    sliderSettings?: SliderSettings;
    recommendation?: SettingsRecommendation;
}
/**
 * Describes application state
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "ApplicationState".
 */
export interface ApplicationState {
    currentPowerMode: ApplicationPowerMode;
    /**
     * Is set to true if GFE ever tried to optimize or customize the game.
     */
    everChangedByGFE: boolean;
    profileAC: SettingsProfileState;
    profileDC: SettingsProfileState;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetApplicationsReturn".
 */
export interface GetApplicationsReturn {
    applications: Application[];
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "RegisterApplicationChangedNotificationsReturn".
 */
export interface RegisterApplicationChangedNotificationsReturn {
    application: Application;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "RegisterApplicationRemovedNotificationsReturn".
 */
export interface RegisterApplicationRemovedNotificationsReturn {
    /**
     * ID of the application that was removed from the list
     */
    localId: number;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "StartApplicationScanParameters".
 */
export interface StartApplicationScanParameters {
    type: ApplicationScanType;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "RegisterApplicationScanProgressNotificationsReturn".
 */
export interface RegisterApplicationScanProgressNotificationsReturn {
    status: Status;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetLastApplicationScanTimeReturn".
 */
export interface GetLastApplicationScanTimeReturn {
    /**
     * Last application scan time in ISO format or empty string if there were no scans
     */
    timestamp: string;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "LaunchApplicationParameters".
 */
export interface LaunchApplicationParameters {
    localId: number;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetLauncherPIDReturn".
 */
export interface GetLauncherPIDReturn {
    /**
     * Return PID of application launcher
     */
    pid: number;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetApplicationSearchPathsReturn".
 */
export interface GetApplicationSearchPathsReturn {
    paths: string[];
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "AddApplicationSearchPathParameters".
 */
export interface AddApplicationSearchPathParameters {
    /**
     * Full directory path in local file system.
     */
    path: string;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "RemoveApplicationSearchPathParameters".
 */
export interface RemoveApplicationSearchPathParameters {
    /**
     * Full directory path in local file system.
     */
    path: string;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetVOPSStatusParameters".
 */
export interface GetVOPSStatusParameters {
    shortName: string;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetVOPSStatusReturn".
 */
export interface GetVOPSStatusReturn {
    status: VopsStatus;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetVOPSSettingsInfoParameters".
 */
export interface GetVOPSSettingsInfoParameters {
    shortName: string;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetVOPSSettingsInfoReturn".
 */
export interface GetVOPSSettingsInfoReturn {
    settingsInfo: VOPSSettingInfo[];
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "RegisterVOPSReadyNotificationsReturn".
 */
export interface RegisterVOPSReadyNotificationsReturn {
    /**
     * Shortname that has VOPS ready
     */
    shortName: string;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetBatteryBoostSupportStateReturn".
 */
export interface GetBatteryBoostSupportStateReturn {
    /**
     * Defines if BatteryBoost is supported
     */
    supported: boolean;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "SetWhisperModeStateParameters".
 */
export interface SetWhisperModeStateParameters {
    enabled: boolean;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetSettingsTranslationsParameters".
 */
export interface GetSettingsTranslationsParameters {
    shortName: string;
    /**
     * Locale format is 'lang_territory', lang is 2 letter code from ISO 639-2, territory is 2-3 letter code from ISO 3166.
     */
    locale: string;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetSettingsTranslationsReturn".
 */
export interface GetSettingsTranslationsReturn {
    translations: SettingTranslation[];
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetSettingsSpaceParameters".
 */
export interface GetSettingsSpaceParameters {
    localId: number;
    tweak: SettingsTweak;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetSettingsSpaceReturn".
 */
export interface GetSettingsSpaceReturn {
    settingsSpace: SettingsSpace;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "SetTargetSettingsParameters".
 */
export interface SetTargetSettingsParameters {
    localId: number;
    powerMode: ApplicationPowerMode;
    position?: SliderPosition;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetApplicationStateParameters".
 */
export interface GetApplicationStateParameters {
    localId: number;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "GetApplicationStateReturn".
 */
export interface GetApplicationStateReturn {
    state: ApplicationState;
}
/**
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "RegisterApplicationStateChangedNotificationsReturn".
 */
export interface RegisterApplicationStateChangedNotificationsReturn {
    /**
     * ID of the application that has its state changed
     */
    localId: number;
}

/**
 * Defines possible scan types
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "ApplicationScanType".
 */
export enum ApplicationScanType {
    fast = 'fast',
    traditional = 'traditional'
}
/**
 * Defines possible VOPS statuses for a game
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "VOPSStatus".
 */
export enum VopsStatus {
    Unknown = 'Unknown',
    NotAvailable = 'NotAvailable',
    NotReady = 'NotReady',
    Ready = 'Ready'
}
/**
 * Defines setting value type
 */
export enum Type {
    STRING = 'STRING',
    INT = 'INT',
    FLOAT = 'FLOAT',
    ENUM = 'ENUM',
    BOOL = 'BOOL',
    DRIVER_ENUM = 'DRIVER_ENUM'
}
/**
 * Defines possible application power modes
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "ApplicationPowerMode".
 */
export enum ApplicationPowerMode {
    AC = 'AC',
    DC = 'DC'
}
/**
 * Defines possible optimization states for profile
 *
 * This interface was referenced by `LocalGames`'s JSON-Schema
 * via the `definition` "OptimizationState".
 */
export enum OptimizationState {
    DISABLED = 'DISABLED',
    UNOPTIMIZED = 'UNOPTIMIZED',
    CUSTOMIZED = 'CUSTOMIZED',
    OPTIMIZED = 'OPTIMIZED'
}
/**
 * Defines current scan status
 */
export enum Status {
    started = 'started',
    finished = 'finished',
    failed = 'failed',
    progress = 'progress'
}


}
